import React, { useState } from "react";
import { OUTLINE_BTN, CARD_TITLE } from "../utils/styles";

const WebsitePerformance = ({ stats }) => {
    const [showAllWebsites, setShowAllWebsites] = useState(false);

    const getProgressColor = (pct) => {
        if (pct >= 41.0) return "#10B981"; // Emerald Green
        if (pct >= 38.0) return "#3B82F6"; // Electric Blue
        return "#6f42c1"; // Theme Purple
    };

    const websites = stats?.websitePerformance || [];
    const hasWebsites = websites.length > 0;

    // Sort websites by Total Calls (descending) by default
    const sortedWebsites = [...websites].sort((a, b) => {
        return (b.totalCall || 0) - (a.totalCall || 0);
    });

    // Clean skeleton approach: when no data, render a placeholder row matching CountryPerformance
    const websitesToRender = hasWebsites
        ? sortedWebsites
        : [{ web_id: "No Data Available", totalCall: 0, callConcluded: 0, callConcludedPercentage: 0 }];

    return (
        <div className="card card-custom h-100 shadow-sm border-0 bg-white rounded-lg p-5 p-md-6">
            <div className="card-header border-0 p-0 mb-5 d-flex justify-content-between align-items-center">
                <h3
                    className="card-title font-weight-bolder text-dark font-size-h5 m-0"
                    style={CARD_TITLE}
                >
                    Website Performance
                </h3>
                {hasWebsites && (
                    <button
                        type="button"
                        onClick={() => setShowAllWebsites(!showAllWebsites)}
                        className="btn btn-outline-primary px-3 py-1.5 rounded-lg font-weight-bolder"
                        style={{ ...OUTLINE_BTN, fontSize: "0.75rem" }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#6f42c1";
                            e.currentTarget.style.color = "#ffffff";
                            e.currentTarget.style.borderColor = "#6f42c1";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#FBF9FF";
                            e.currentTarget.style.color = "#6f42c1";
                            e.currentTarget.style.borderColor = "#EFEBFC";
                        }}
                    >
                        {showAllWebsites ? "View Less" : "View All"}
                    </button>
                )}
            </div>
            <div className="card-body p-0">
                <div
                    style={{
                        maxHeight: showAllWebsites ? "260px" : "none",
                        overflowY: showAllWebsites ? "auto" : "visible",
                        overflowX: "auto",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#6f42c1 transparent",
                        minHeight: "200px"
                    }}
                    className="w-100 d-flex flex-column"
                >
                    <table
                        className="table table-borderless table-vertical-center m-0"
                        style={{ width: "100%" }}
                    >
                        <thead>
                            <tr
                                className="text-dark font-weight-boldest border-bottom border-light"
                                style={{ fontSize: "0.70rem", letterSpacing: "0.7px", color: "#A1A5B7", backgroundColor: "#ffffff" }}
                            >
                                <th className="pl-0 pb-3" style={{ position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 10 }}>
                                    Website Name
                                </th>
                                <th className="text-center pb-3" style={{ position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 10 }}>
                                    Total Calls
                                </th>
                                <th className="text-center pb-3" style={{ position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 10 }}>
                                    Concluded Calls
                                </th>
                                <th className="text-right pb-3" style={{ position: "sticky", top: 0, backgroundColor: "#ffffff", zIndex: 10 }}>
                                    Concluded %
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {websitesToRender
                                .filter((row, idx) => (showAllWebsites ? true : idx < 5))
                                .map((row, idx) => {
                                    const concludedPct = Number(row.callConcludedPercentage || 0);
                                    return (
                                        <tr
                                            key={idx}
                                            className="border-bottom border-light-soft"
                                            style={{ transition: "all 0.25s ease-in-out", cursor: "pointer" }}
                                            onMouseEnter={(e) => {
                                                if (hasWebsites) {
                                                    e.currentTarget.style.backgroundColor = "rgba(110, 66, 193, 0.03)";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (hasWebsites) {
                                                    e.currentTarget.style.backgroundColor = "transparent";
                                                }
                                            }}
                                        >
                                            <td
                                                className="pl-0 py-3 font-weight-boldest text-dark"
                                                style={{
                                                    fontSize: "0.85rem",
                                                    color: hasWebsites ? "#181C32" : "#B5B5C3",
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-all",
                                                    lineHeight: "1.3",
                                                    maxWidth: "180px"
                                                }}
                                                title={row.web_id || "Unknown"}
                                            >
                                                {row.web_id || "Unknown"}
                                            </td>
                                            <td className="text-center py-3 font-weight-bolder text-dark-75" style={{ fontSize: "0.85rem", color: hasWebsites ? "#3F4254" : "#D3D3D3", whiteSpace: "nowrap" }}>
                                                {hasWebsites ? (row.totalCall || 0).toLocaleString() : "0"}
                                            </td>
                                            <td className="text-center py-3 font-weight-bolder text-dark-75" style={{ fontSize: "0.85rem", color: hasWebsites ? "#3F4254" : "#D3D3D3", whiteSpace: "nowrap" }}>
                                                {hasWebsites ? (row.callConcluded || 0).toLocaleString() : "0"}
                                            </td>
                                            <td className="text-right py-3 font-size-sm" style={{ whiteSpace: "nowrap" }}>
                                                <div className="d-flex align-items-center justify-content-end">
                                                    <span
                                                        className="font-weight-boldest text-dark mr-2"
                                                        style={{ fontSize: "0.82rem", minWidth: "38px", color: hasWebsites ? "#181C32" : "#B5B5C3" }}
                                                    >
                                                        {hasWebsites ? `${concludedPct.toFixed(2)}%` : "0.00%"}
                                                    </span>
                                                    <div
                                                        className="progress"
                                                        style={{ height: "6px", width: "40px", flex: "0 0 40px", backgroundColor: "#F3F4F6", borderRadius: "10px" }}
                                                    >
                                                        <div
                                                            className="progress-bar"
                                                            role="progressbar"
                                                            style={{
                                                                width: `${concludedPct}%`,
                                                                backgroundColor: hasWebsites ? getProgressColor(concludedPct) : "#E4E6EF",
                                                                borderRadius: "10px",
                                                                transition: "width 0.4s ease",
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default React.memo(WebsitePerformance);
